import { AutocompleteField } from '@eventbrite/eds-autocomplete-field';
import { Icon } from '@eventbrite/eds-icon';
import { IconButton } from '@eventbrite/eds-icon-button';
import {
    CrossChunky as CrossChunkySvg,
    MagnifyingGlassChunky as MagnifyingGlassChunkySvg,
} from '@eventbrite/eds-iconography';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import {
    parseHistorySuggestions,
    storeQuery,
} from '@eventbrite/redux-destination';
import { $FixMe } from '@eventbrite/ts-utils';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import React, { PureComponent } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import {
    CATEGORY_DROPDOWN_SELECT,
    EVENTBRITE_CATEGORY,
    EVENT_TYPE,
} from '../../../../../../constants';
import {
    GA_VIEW_EVENT_AUTOCOMPLETE,
    SEARCH_SECTION,
} from '../../../../../../constants/analytics';
import { SEARCH_QUERY_MAX_LENGTH } from '../../../../../../constants/constants';
import { SuggestionsState } from '../../../../../../types';
import { openEventUrl } from '../../../../../../utils/http';

const EVENT_DEBOUNCE_INTERVAL = 200;
const placeholder = gettext('Search anything');

interface SearchAutocompleteProps {
    hasHistory?: boolean;
    suggestions: SuggestionsState;
    searchQuery?: string;
    shouldFocusInput?: boolean;
    onSearchSuggestionChange?: Function;
    onSearchFilterChange?: Function;
    onSearchQueryChange?: Function;
    onClearButtonClick?: Function;
    onFocus?: Function;
    onBlur?: Function;
    trackGAEvent?: Function;
    history: RouteComponentProps['history'];
}

export class SearchAutocomplete extends PureComponent<SearchAutocompleteProps> {
    _handleSearchAutocompleteSelect = (suggestions: $FixMe, selected = '') => {
        const { onSearchFilterChange } = this.props;

        //User selected category from dropdown
        if (selected.indexOf(EVENTBRITE_CATEGORY) > -1) {
            return this._handleCategorySelect(selected);
        }

        //User selected an event from the dropdown
        if (selected.indexOf(`${EVENT_TYPE}/`) > -1) {
            const eid = selected.replace(`${EVENT_TYPE}/`, '');

            return this._handleEventSelect(eid, suggestions);
        }

        //User selected a query from the suggested options
        //So we should set the state of the component and run the expected search
        return onSearchFilterChange?.({ q: selected });
    };

    _handleSearchAutocompleteChange = (searchQuery: string) => {
        const { onSearchQueryChange } = this.props;

        onSearchQueryChange?.(searchQuery);

        this._requestSearchSuggestions(searchQuery);
    };

    _requestSearchSuggestions = debounce((searchQuery) => {
        const { onSearchSuggestionChange } = this.props;

        onSearchSuggestionChange?.(searchQuery);
    }, EVENT_DEBOUNCE_INTERVAL);

    _handleCategorySelect = (value: string) => {
        const { onSearchFilterChange } = this.props;

        // If the user has selected a category from the
        // search dropdown, that implies their search
        // query is not relevant anymore
        onSearchFilterChange?.({ tags: value, q: '' });
    };

    _handleEventSelect = (eventId: string, suggestions: $FixMe) => {
        const { hasHistory, searchQuery, trackGAEvent } = this.props;
        const suggestedEvent = find(suggestions, { value: `event/${eventId}` });

        if (!isEmpty(suggestedEvent)) {
            const { ticketsBy, url } = suggestedEvent;

            trackGAEvent?.({
                category: SEARCH_SECTION,
                action: GA_VIEW_EVENT_AUTOCOMPLETE,
                label: eventId,
            });

            if (hasHistory && HAS_WINDOW) {
                storeQuery(searchQuery);
            }

            openEventUrl(url, ticketsBy);
        }
    };

    _handleKeyDown = ({
        event,
    }: {
        event: { charCode: number; keyCode: number };
    }) => {
        const { hasHistory, searchQuery } = this.props;
        const isEnterKey = event.charCode === 13 || event.keyCode === 13;

        if (hasHistory && HAS_WINDOW) {
            if (isEnterKey) {
                storeQuery(searchQuery);
            }
        }
    };

    render() {
        const {
            suggestions: { query = [], event = [], events = [] },
            searchQuery,
            onClearButtonClick,
            onFocus,
            onBlur,
            shouldFocusInput,
        } = this.props;
        const historySuggestions = parseHistorySuggestions({
            inputValue: searchQuery,
        });

        let clearSearchButton = null;

        /* Why needed: when a user selects a category from the dropdown, the
        passed through value never changes from an empty string so
        a re-render does not occur. This creates odd interactions and
        unintended behaviors. So instead utilizing a whitelisted value */
        const queryToUse =
            searchQuery === CATEGORY_DROPDOWN_SELECT ? '' : searchQuery;

        let suggestions = [...historySuggestions, ...events];

        if (!isEmpty(searchQuery) && searchQuery !== CATEGORY_DROPDOWN_SELECT) {
            suggestions = [...historySuggestions, ...query, ...event];
        }

        if (searchQuery) {
            clearSearchButton = (
                <span className="eds-show-down-sw">
                    <IconButton
                        iconType={<CrossChunkySvg />}
                        title={gettext('Clear search')}
                        onClick={onClearButtonClick}
                    />
                </span>
            );
        }

        const magnifyingIcon = (
            <Icon
                color="grey-800"
                size="small"
                type={<MagnifyingGlassChunkySvg />}
            />
        );

        return (
            <div className="eds-l-mar-2" data-spec="event-autocomplete">
                <AutocompleteField
                    id="eventPicker"
                    name="eventPicker"
                    type="search"
                    suggestions={suggestions}
                    value={queryToUse}
                    suffix={clearSearchButton}
                    prefix={magnifyingIcon}
                    onChange={this._handleSearchAutocompleteChange}
                    onSelect={this._handleSearchAutocompleteSelect.bind(
                        null,
                        suggestions,
                    )}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onKeyDown={this._handleKeyDown}
                    shouldFocusInput={shouldFocusInput}
                    hideOutline={true}
                    isV2={true}
                    borderType="none"
                    placeholder={placeholder}
                    preventInputValuePrefill={true}
                    maxLength={SEARCH_QUERY_MAX_LENGTH}
                />
            </div>
        );
    }
}
